/**
 * OnboardPublicView -- la ficha publica con la que un dueno registra su negocio.
 * Ruta: #/onboard/{token}. Sin sesion (showPublic en main.js).
 *
 * AUDIENCIA: el dueno de un negocio nuevo, desde el enlace que le llega por WhatsApp. Casi
 * siempre en el CELULAR y sin nadie de Nexo al lado.
 *
 * ONB-FICHA-FORMULARIO-T1 (D-37 = A, Josse 21-sep): deja de ser un chat guionado y pasa a un
 * formulario por pasos -- tu negocio / donde esta / tus datos / autorizaciones --, con lo ya
 * conocido lleno, un resumen para verificar y editar antes de enviar, y el PIN con su regla
 * dicha antes de pedirlo. La regla vive en application/onboarding/formularioPorPasos.js.
 *
 * Los mismos endpoints de siempre: GET token (retoma), POST responses por paso (retoma entre
 * dispositivos) y completo al confirmar, POST pin. El PIN va por su propio camino
 * (onboardPin.js): nunca entra a los valores del formulario, al borrador ni a responses.
 *
 * @module presentation/views/OnboardPublicView
 */

// SIN-VUELTA: es publica y se abre SIN sesion, desde un enlace que recibe un cliente nuevo. No hay dashboard al que volver: mandarlo ahi lo llevaria a un login que no tiene.
// Lo declara la pantalla y no una lista en el build: la razon tiene que estar
// donde alguien la lea el dia que se pregunte por que esta no tiene salida.

import { AppConfig } from '../../config.js';
import { OnboardingPublicoApiClient } from '../../infrastructure/api/OnboardingPublicoApiClient.js';
import { leerCatalogo } from '../../application/negocios/catalogo.js';
import { consentimientosQueAplican } from '../../application/onboarding/consentimiento.js';
// ONB-PIN-CHAT-T1: el PIN vive en su propio modulo. Nunca entra a los valores, al borrador ni a responses.
import {
  PIN_ONBOARD, CIERRE_SIN_PIN, PREGUNTA_PIN, ECO_PIN, sesionCompleta, pinPublicadoSegun, crearConversacionDelPin
} from '../../application/onboarding/onboardPin.js';
// ONB-CUENTA-PANEL-T1: la cuenta del panel del dueno al cerrar (correo y clave; la clave solo viaja en su POST).
import { CLAVE_MINIMA, erroresDeLaCuenta, leerRespuestaDeCuenta } from '../../application/onboarding/cuentaPanel.js';
import { porQueNoSubeElLogo, leerRespuestaDeLogo, logoServidoSegun, LOGO_QUEDA } from '../../application/onboarding/logoOnboarding.js';
// #15604: la MISMA regla del API para el correo y los telefonos, y la lectura de su 400.
import { claveDelRechazo, clavesParaRepreguntar, MENSAJES_DE_CONTACTO } from '../../application/onboarding/contactoDelNegocio.js';
// ONB-FICHA-MOVIL-T1: retomar por el nombre lo que Argemiro ya pregunto; lo tecnico solo para quien depura.
import { hayRetoma, saludoDeRetoma, progresoCorto, muestraLoTecnico } from '../../application/onboarding/saludoDeRetoma.js';
import {
  CAMPOS_REQUERIDOS, CONSENTIMIENTOS, REGLA_DEL_PIN, OTRA_CIUDAD,
  pasosDelFormulario, camposVisibles, precarga, erroresDelPaso, primerPasoPendiente, pasoDeLaClave,
  cuerpoDeRespuestas, resumen, ciudadesPara, municipioCanon, PLANTILLAS_HORARIO, textoDelHorario
} from '../../application/onboarding/formularioPorPasos.js';

// Deuda 4: este escape (no cambiaba la comilla simple) es ahora el compartido.
import { esc } from '../shared/texto.js';
import { leerSesionDelEnlace, desenlaceDelEnvio, ENLACE_VENCIDO, NO_SE_GUARDO } from '../../application/onboarding/envioDelRegistro.js';
// CLI-ACT-T1 (nexo-api 717): el cliente que ya esta confirma o corrige lo que Nexo tiene, campo por campo.
import {
  CAMPOS_CONFIRMABLES, ENMASCARADOS, TEXTOS_CONFIRMACION, leerModo, valoresDeConfirmacion, confirmados, corregir,
  dejarComoEstaba, valorAlDejar, sinErroresDeConfirmados, cuerpoConCorregidos, valoresParaMostrar
} from '../../application/onboarding/confirmacionDelRegistro.js';

// ONB-FICHA-MOVIL-T2 (#16253), ahora en el formulario: sin desborde, alto real del celular
// (100dvh), entradas de 16 px (Safari no hace zoom), boton fijo abajo respetando el borde del
// iPhone. Todo con alcance .onb-f para no tocar el resto del panel.
const ESTILO = `
  .onb-f { box-sizing:border-box; min-height:100dvh; display:flex; flex-direction:column; max-width:560px; margin:0 auto; background:var(--bg-canvas); color:var(--text-strong); overflow-x:hidden; }
  .onb-f *, .onb-f *::before, .onb-f *::after { box-sizing:border-box; }
  .onb-f__cabeza { padding:12px 16px 8px; border-bottom:1px solid var(--border-default); }
  .onb-f__marca { display:flex; justify-content:space-between; align-items:center; gap:8px; }
  .onb-f__marca strong { font-size:1rem; }
  .onb-f__presenta { margin:6px 0 0; font-size:0.875rem; color:var(--text-secondary); line-height:1.4; }
  .onb-f__pasos { display:flex; gap:6px; margin-top:10px; }
  .onb-f__pasos span { flex:1; height:4px; border-radius:2px; background:var(--bg-tertiary); }
  .onb-f__pasos span.hecho { background:#2f81f7; }
  .onb-f__estado { display:flex; justify-content:space-between; gap:8px; margin-top:6px; font-size:0.875rem; color:var(--text-secondary); }
  .onb-f__cuerpo { flex:1; padding:14px 16px 24px; overflow-y:auto; }
  .onb-f__retoma { background:#1f6feb1a; border:1px solid #1f6feb55; border-radius:10px; padding:10px 12px; font-size:0.875rem; line-height:1.4; margin-bottom:12px; overflow-wrap:anywhere; }
  .onb-f__campo { margin-bottom:14px; }
  .onb-f__campo label, .onb-f__pregunta { display:block; font-size:0.875rem; color:var(--text-heading); margin-bottom:6px; }
  .onb-f__ayuda { font-size:0.875rem; color:var(--text-secondary); margin-top:4px; }
  .onb-f__error { font-size:0.875rem; color:var(--accent-red); margin-top:4px; }
  .onb-f input[type=text], .onb-f input[type=tel], .onb-f input[type=email], .onb-f input[type=time], .onb-f input[type=password], .onb-f select {
    width:100%; max-width:100%; font-size:16px; padding:12px 14px; border-radius:10px; border:1px solid var(--border-default); background:var(--bg-secondary); color:var(--text-strong); }
  .onb-f__sino { display:flex; gap:8px; }
  .onb-f__sino label { flex:1; margin:0; text-align:center; padding:12px; border:1px solid var(--border-default); border-radius:10px; font-size:16px; }
  .onb-f__sino input { position:absolute; opacity:0; pointer-events:none; }
  .onb-f__sino input:checked + span { color:var(--text-on-accent); }
  .onb-f__sino label:has(input:checked) { background:var(--accent-blue-strong); border-color:var(--accent-blue-strong); }
  .onb-f__check { display:flex; gap:10px; align-items:flex-start; font-size:.9rem; line-height:1.4; }
  .onb-f__check input { width:22px; height:22px; flex:0 0 auto; margin-top:1px; }
  /* ONB-FICHA-REGISTRO-T1: .onb-f__campo label (display:block) le ganaba a .onb-f__check y la casilla quedaba sobre el texto. */
  .onb-f__campo label.onb-f__check { display:flex; margin-bottom:0; }
  .onb-f__horas { display:flex; gap:10px; margin-top:8px; }
  .onb-f__horas label { flex:1; min-width:0; margin:0; font-size:0.875rem; color:var(--text-secondary); }
  .onb-f__horas input { margin-top:4px; }
  .onb-f__enlace { background:none; border:0; padding:8px 0 0; color:var(--accent-blue); font-size:0.875rem; text-decoration:underline; cursor:pointer; }
  .onb-f__previa { font-size:0.875rem; color:var(--text-heading); margin-top:6px; }
  .onb-f__resumen { background:var(--bg-secondary); border:1px solid var(--border-default); border-radius:10px; padding:10px 12px; margin-bottom:10px; }
  .onb-f__resumen h3 { display:flex; justify-content:space-between; align-items:center; margin:0 0 6px; font-size:0.875rem; }
  .onb-f__fila { display:flex; justify-content:space-between; gap:10px; font-size:0.875rem; padding:3px 0; }
  .onb-f__fila span:first-child { color:var(--text-secondary); }
  .onb-f__fila span:last-child { text-align:right; overflow-wrap:anywhere; }
  .onb-f__regla { background:var(--bg-secondary); border:1px solid var(--border-default); border-radius:10px; padding:10px 12px; font-size:0.875rem; line-height:1.45; margin-bottom:14px; }
  .onb-f__pie { position:sticky; bottom:0; display:flex; gap:10px; padding:12px 16px calc(12px + env(safe-area-inset-bottom)); border-top:1px solid var(--border-default); background:var(--bg-canvas); }
  .onb-f__pie button { font-size:16px; padding:14px; border-radius:12px; border:0; }
  .onb-f__pie .primario { flex:1; background:#2f81f7; color:var(--text-on-accent); font-weight:600; }
  .onb-f__pie .secundario { background:transparent; border:1px solid var(--border-default); color:var(--text-strong); }
  .onb-f__pie button:disabled { opacity:.6; }
  /* ONB-CUENTA-PANEL-T1: el boton de la cuenta va en el cuerpo de la pantalla final (el pie queda vacio): el mismo estilo. */
  .onb-f__cuenta .primario { width:100%; font-size:16px; padding:14px; border-radius:12px; border:0; background:#2f81f7; color:var(--text-on-accent); font-weight:600; }
  .onb-f__cuenta .primario:disabled { opacity:.6; }
  .onb-f__final { font-size:.95rem; line-height:1.5; overflow-wrap:anywhere; }
  .onb-f__tiene { font-size:16px; padding:12px 14px; border-radius:10px; border:1px dashed var(--border-default); background:var(--bg-secondary); overflow-wrap:anywhere; }
  .onb-f__tiene span { color:var(--text-secondary); font-size:0.875rem; }
  .onb-f__tecnico { margin:0 16px 16px; font-size:0.875rem; color:var(--text-secondary); overflow-wrap:anywhere; }
`;

// TIPO-NEGOCIO-CANON-T1: el config es publico (AllowAnonymous) y esta pagina no
// tiene sesion, asi que va con fetch y NO con ApiBase: un 401 en ApiBase cierra
// la sesion y recarga, que aqui no tiene sentido.
async function fetchCatalogo() {
  try {
    const res = await OnboardingPublicoApiClient.pedir(`/api/businesses/config`, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) {
      const fallo = leerCatalogo({ ok: false, code: res.status });
      try {
        const cuerpo = await res.json();
        fallo.facturacionElectronicaOfrecida = cuerpo?.facturacion_electronica_ofrecida === true;
      } catch { /* sin cuerpo */ }
      return fallo;
    }
    return leerCatalogo({ ok: true, data: await res.json() });
  } catch {
    return leerCatalogo({ ok: false, code: 'NETWORK' });
  }
}

async function fetchTokenSession(token) {
  try {
    const res = await OnboardingPublicoApiClient.pedir(`/api/v1/onboarding/token/${encodeURIComponent(token)}`, {
      headers: { 'Accept': 'application/json' }
    });
    if (res.ok) {
      const body = await res.json();
      return { ok: true, data: body?.data || body, mock: false };
    }
    // Ares #38558: un enlace vencido (404/410) se dice; no se arma un borrador falso sobre el (5xx si: el servidor
    // puede volver y el borrador del celular es el respaldo).
    if (leerSesionDelEnlace(res.status) === 'vencido') return { ok: false, vencido: true };
  } catch { /* sin red: se trabaja con el borrador del dispositivo */ }
  return { ok: true, mock: true, data: { token, status: 'pending', createdAt: new Date().toISOString(), capturedFields: {} } };
}

export async function renderOnboardPublic(container) {
  const hash = window.location.hash || '';
  const token = hash.replace(/^#\/onboard\//, '').split('?')[0] || '';
  // ONB-FICHA-MOVIL-T1: el token y la lista de campos son de quien depura: ?dev=1 o admin.
  const loTecnico = muestraLoTecnico({
    hash,
    busqueda: window.location.search || '',
    esAdmin: AppConfig.role === 'admin' || AppConfig.isAdmin === true
  });

  container.innerHTML = `
    <section class="onb-f" data-onboard-formulario>
      <style>${ESTILO}</style>
      <div class="onb-f__cabeza">
        <div class="onb-f__marca">
          <strong>Registra tu negocio</strong>
          <button class="btn-ghost btn-sm" id="onboard-exit">Salir y retomar luego</button>
        </div>
        <p class="onb-f__presenta" data-onboard-presentacion>Te acompaña Argemiro, el asistente de inteligencia artificial de Nexo Digital. Al final, una persona de Nexo revisa todo.</p>
        <div class="onb-f__pasos" id="onb-f-barras"></div>
        <div class="onb-f__estado">
          <span id="onb-f-paso">Cargando…</span>
          <span id="onboard-progress-corto"></span>
        </div>
      </div>
      <div class="onb-f__cuerpo" id="onb-f-cuerpo"></div>
      <div class="onb-f__pie" id="onb-f-pie"></div>
      ${loTecnico ? `<div class="onb-f__tecnico" data-onboard-tecnico>Token: <code>${esc(token || '—')}</code> · campos: <span id="onboard-count"></span></div>` : ''}
    </section>`;

  const $barras = container.querySelector('#onb-f-barras');
  const $paso = container.querySelector('#onb-f-paso');
  const $corto = container.querySelector('#onboard-progress-corto');
  const $cuerpo = container.querySelector('#onb-f-cuerpo');
  const $pie = container.querySelector('#onb-f-pie');
  const $count = container.querySelector('#onboard-count');
  container.querySelector('#onboard-exit').addEventListener('click', () => _showExitModal(token));

  const [sess, catalogo] = await Promise.all([fetchTokenSession(token), fetchCatalogo()]);
  if (sess.vencido) {
    $paso.textContent = 'Enlace vencido';
    $cuerpo.innerHTML = `<div class="onb-f__error" data-enlace-vencido style="margin-top:18px">${esc(ENLACE_VENCIDO)}</div>`;
    $pie.innerHTML = '';
    return;
  }
  // ONB-DIAN-PARAMETRO-T1: solo los consentimientos que la API ofrece hoy.
  const consentimientos = consentimientosQueAplican(CONSENTIMIENTOS, catalogo.facturacionElectronicaOfrecida);
  const pasos = pasosDelFormulario({ tipos: catalogo.tipos, consentimientos });

  // Lo ya conocido (backend o borrador del dispositivo) llega lleno.
  const draftKey = `nx_onboard_draft_${token}`;
  let guardado = {};
  try { guardado = JSON.parse(localStorage.getItem(draftKey) || '{}'); } catch { guardado = {}; }
  // CLI-ACT-T1: en confirmacion arranca con lo que Nexo tiene (sin lo enmascarado); lo escrito por el cliente manda.
  const { modo, actual: ficha } = leerModo(sess.data);
  const delCliente = { ...guardado, ...(sess.data?.capturedFields || {}) };
  const captured = precarga(modo === 'confirmacion' ? valoresDeConfirmacion(ficha, delCliente) : delCliente);
  const corrKey = `nx_onboard_corr_${token}`;
  let corregidos = new Set();
  try { corregidos = new Set(JSON.parse(localStorage.getItem(corrKey) || '[]').filter((k) => CAMPOS_CONFIRMABLES.includes(k))); } catch { corregidos = new Set(); }
  const guardarCorregidos = () => { try { localStorage.setItem(corrKey, JSON.stringify([...corregidos])); } catch { /* quota */ } };
  const conf = () => confirmados(modo, ficha, corregidos);
  const erroresDe = (i) => sinErroresDeConfirmados(erroresDelPaso(pasos[i], captured), conf());
  const pendiente = () => primerPasoPendiente(pasos, captured, conf());
  const cuerpo = (claves) => cuerpoConCorregidos(modo, cuerpoDeRespuestas(captured, claves), conf(), corregidos);
  if (modo === 'confirmacion') container.querySelector('.onb-f__marca strong').textContent = TEXTOS_CONFIRMACION.titulo;

  const guardarBorrador = () => { try { localStorage.setItem(draftKey, JSON.stringify(captured)); } catch { /* quota */ } };

  let actual = primerPasoPendiente(pasos, captured, conf());
  // CLI-ACT-T1: en confirmacion se revisa todo desde el primer paso, y el saludo de -sigamos con lo que falta- no aplica.
  if (modo === 'confirmacion') actual = 0;
  const retoma = modo !== 'confirmacion' && hayRetoma(captured, CAMPOS_REQUERIDOS) ? saludoDeRetoma(captured, CAMPOS_REQUERIDOS, fieldLabel) : null;
  let conversacionPin = null;

  function pintarEncabezado(etiqueta, hechos) {
    $barras.innerHTML = pasos.map((_, i) => `<span class="${i < hechos ? 'hecho' : ''}"></span>`).join('');
    $paso.textContent = etiqueta;
    // CLI-ACT-T1: lo confirmado de la ficha cuenta como hecho (enmascarado no entra a captured, pero esta).
    const vistos = valoresParaMostrar(captured, ficha, conf());
    $corto.textContent = progresoCorto(vistos, CAMPOS_REQUERIDOS);
    if ($count) {
      const hechosCampos = CAMPOS_REQUERIDOS.filter((k) => vistos[k] != null && String(vistos[k]).trim() !== '').length;
      $count.textContent = `${hechosCampos} / ${CAMPOS_REQUERIDOS.length}`;
    }
  }

  function htmlDelCampo(c, errores) {
    const val = captured[c.clave];
    const error = errores[c.clave] ? `<div class="onb-f__error" data-error="${c.clave}">${esc(errores[c.clave])}</div>` : '';
    const ayuda = c.ayuda ? `<div class="onb-f__ayuda">${esc(c.ayuda)}</div>` : '';
    const id = `onb-f-${c.clave}`;
    if (c.tipo === 'check') {
      return `<div class="onb-f__campo" data-campo="${c.clave}"><label class="onb-f__check"><input type="checkbox" name="${c.clave}" ${val === true ? 'checked' : ''}><span>${esc(c.etiqueta)}${c.requerido ? '' : ' <small>(opcional)</small>'}</span></label>${error}</div>`;
    }
    if (c.tipo === 'sino') {
      return `<div class="onb-f__campo" data-campo="${c.clave}"><span class="onb-f__pregunta">${esc(c.etiqueta)}</span>
        <div class="onb-f__sino">
          <label><input type="radio" name="${c.clave}" value="si" ${val === true ? 'checked' : ''}><span>Sí</span></label>
          <label><input type="radio" name="${c.clave}" value="no" ${val === false ? 'checked' : ''}><span>No</span></label>
        </div>${ayuda}${error}</div>`;
    }
    // ONB-FICHA-REGISTRO-T1 (2): el municipio se escribe y autocompleta de la lista del departamento,
    // en orden alfabetico; si no esta, -Mi municipio no esta en la lista- lo deja escribir aparte.
    if (c.tipo === 'ciudad') {
      if (val === OTRA_CIUDAD) {
        return `<div class="onb-f__campo" data-campo="${c.clave}"><label for="onb-f-cityOtra">${esc(c.etiqueta)}</label>
          <input type="text" name="cityOtra" id="onb-f-cityOtra" placeholder="Escribe tu ciudad o municipio" value="${esc(captured.cityOtra || '')}">
          <button type="button" class="onb-f__enlace" data-ciudad-lista>Buscarlo en la lista</button>${ayuda}${error}</div>`;
      }
      const sinDepartamento = !captured.department;
      return `<div class="onb-f__campo" data-campo="${c.clave}"><label for="${id}">${esc(c.etiqueta)}</label>
        <input type="text" id="${id}" name="${c.clave}" list="onb-f-municipios" autocomplete="off" ${sinDepartamento ? 'disabled' : ''}
          placeholder="${sinDepartamento ? 'Primero elige el departamento' : 'Empieza a escribir y elige de la lista'}" value="${esc(val ?? '')}">
        <datalist id="onb-f-municipios">${ciudadesPara(captured).map((o) => `<option value="${esc(o)}"></option>`).join('')}</datalist>
        ${sinDepartamento ? '' : '<button type="button" class="onb-f__enlace" data-ciudad-otra>Mi municipio no está en la lista</button>'}${ayuda}${error}</div>`;
    }
    // ONB-FICHA-REGISTRO-T1 (3): el horario por plantilla (dias + abre + cierra); lo que viaja es el mismo texto de antes.
    if (c.tipo === 'horario') {
      const plantilla = captured.horarioPlantilla || '';
      const conHoras = plantilla && plantilla !== 'personalizado';
      const previa = conHoras ? textoDelHorario({ plantilla, abre: captured.horarioAbre, cierra: captured.horarioCierra }) : null;
      return `<div class="onb-f__campo" data-campo="${c.clave}"><label for="onb-f-horarioPlantilla">${esc(c.etiqueta)}</label>
        <select id="onb-f-horarioPlantilla" name="horarioPlantilla">
          <option value="">Elige los días…</option>
          ${PLANTILLAS_HORARIO.map((p) => `<option value="${p.id}" ${p.id === plantilla ? 'selected' : ''}>${esc(p.etiqueta)}</option>`).join('')}
        </select>
        ${conHoras ? `<div class="onb-f__horas">
          <label>Abre<input type="time" name="horarioAbre" value="${esc(captured.horarioAbre || '')}"></label>
          <label>Cierra<input type="time" name="horarioCierra" value="${esc(captured.horarioCierra || '')}"></label>
        </div>
        <div class="onb-f__previa" data-horario-previa>${previa ? 'Quedará así: ' + esc(previa) : ''}</div>` : ''}
        ${plantilla === 'personalizado' ? `<input type="text" id="${id}" name="horario" style="margin-top:8px" placeholder="Ej: Lun-Vie 7am-6pm, Sáb 8am-1pm" value="${esc(val ?? '')}">` : ''}
        ${ayuda}${error}</div>`;
    }
    if (c.tipo === 'select') {
      const opciones = c.opciones || [];
      const actualVal = String(val ?? '');
      return `<div class="onb-f__campo" data-campo="${c.clave}"><label for="${id}">${esc(c.etiqueta)}</label>
        <select id="${id}" name="${c.clave}">
          <option value="">Elige…</option>
          ${opciones.map((o) => `<option value="${esc(o)}" ${o === actualVal ? 'selected' : ''}>${esc(o)}</option>`).join('')}
        </select>${ayuda}${error}</div>`;
    }
    const inputmode = c.inputmode ? `inputmode="${c.inputmode}"` : '';
    const auto = c.autocomplete ? `autocomplete="${c.autocomplete}"` : '';
    const tipo = c.tipo === 'email' ? 'email' : c.tipo === 'tel' ? 'tel' : 'text';
    return `<div class="onb-f__campo" data-campo="${c.clave}"><label for="${id}">${esc(c.etiqueta)}</label>
      <input id="${id}" type="${tipo}" name="${c.clave}" ${inputmode} ${auto} value="${esc(val ?? '')}">${ayuda}${error}</div>`;
  }

  // CLI-ACT-T1: lo que Nexo ya tiene se muestra con -Corregir-; lo corregido abre su campo con -Dejar como estaba-.
  function htmlDelCampoConFicha(c, errores) {
    const tiene = ficha[c.clave];
    if (conf().has(c.clave)) {
      return `<div class="onb-f__campo" data-campo="${c.clave}" data-confirmado="${c.clave}"><span class="onb-f__pregunta">${esc(c.etiqueta)}</span>
        <div class="onb-f__tiene"><span>${esc(TEXTOS_CONFIRMACION.tiene)}:</span> <strong data-tiene="${c.clave}">${esc(tiene)}</strong></div>
        <button type="button" class="onb-f__enlace" data-corregir="${c.clave}">${esc(TEXTOS_CONFIRMACION.corregir)}</button></div>`;
    }
    const base = htmlDelCampo(c, errores);
    if (modo !== 'confirmacion' || !corregidos.has(c.clave) || !tiene) return base;
    const extra = `${ENMASCARADOS.includes(c.clave) ? `<div class="onb-f__ayuda" data-escribir-completo>${esc(TEXTOS_CONFIRMACION.completo)}</div>` : ''}
      <button type="button" class="onb-f__enlace" data-dejar="${c.clave}">${esc(TEXTOS_CONFIRMACION.dejar)}</button>`;
    return base.replace(/<\/div>$/, extra + '</div>');
  }

  function pintarPaso(i, errores = {}, aviso = '') {
    actual = i;
    const paso = pasos[i];
    pintarEncabezado(`Paso ${i + 1} de ${pasos.length} · ${paso.titulo}`, i);
    $cuerpo.innerHTML = `
      ${modo === 'confirmacion' ? `<div class="onb-f__retoma" data-onboard-confirmacion>${esc(TEXTOS_CONFIRMACION.intro)}</div>` : ''}
      ${retoma && i === pendiente() ? `<div class="onb-f__retoma" data-onboard-retoma>${esc(retoma)}</div>` : ''}
      ${aviso ? `<div class="onb-f__retoma">${esc(aviso)}</div>` : ''}
      ${camposVisibles(paso, captured).map((c) => htmlDelCampoConFicha(c, errores)).join('')}
      ${paso.id === 'negocio' ? '<div data-logo-host></div>' : ''}`;
    if (paso.id === 'negocio') ofrecerLogo($cuerpo.querySelector('[data-logo-host]'));
    $pie.innerHTML = `
      ${i > 0 ? '<button type="button" class="secundario" data-accion="atras">Atrás</button>' : ''}
      <button type="button" class="primario" data-accion="siguiente">${i === pasos.length - 1 ? 'Revisar' : 'Siguiente'}</button>`;
    const primerError = Object.keys(errores)[0];
    if (primerError) $cuerpo.querySelector(`[name="${primerError}"]`)?.focus();
  }

  function pintarResumen(aviso = '') {
    actual = pasos.length;
    pintarEncabezado('Revisa antes de enviar', pasos.length);
    $cuerpo.innerHTML = `
      ${aviso ? `<div class="onb-f__retoma">${esc(aviso)}</div>` : ''}
      <p style="font-size:0.875rem;color:var(--text-secondary);margin:0 0 10px">Revisa que todo esté bien. Puedes corregir cualquier parte.</p>
      ${resumen(pasos, valoresParaMostrar(captured, ficha, conf())).map((b) => `
        <div class="onb-f__resumen" data-resumen="${b.indice}">
          <h3><span>${esc(b.paso)}</span><button type="button" class="btn-ghost btn-sm" data-editar="${b.indice}">Editar</button></h3>
          ${b.filas.map(([k, v]) => `<div class="onb-f__fila"><span>${esc(k)}</span><span>${esc(v)}</span></div>`).join('')}
        </div>`).join('')}`;
    $pie.innerHTML = `
      <button type="button" class="secundario" data-accion="atras">Atrás</button>
      <button type="button" class="primario" data-accion="confirmar">Confirmar y enviar</button>`;
  }

  function pintarPin(mensaje = '') {
    pintarEncabezado('Último paso · tu PIN', pasos.length);
    $cuerpo.innerHTML = `
      <div class="onb-f__regla" data-regla-pin>${esc(REGLA_DEL_PIN)}<br>Con tu cédula y este PIN entras al programa. No lo compartas: ni siquiera nosotros te lo vamos a pedir.</div>
      ${mensaje ? `<div class="onb-f__error" data-pin-mensaje style="margin-bottom:12px">${esc(mensaje)}</div>` : ''}
      <div class="onb-f__campo"><label for="onb-f-pin1">Crea tu PIN</label>
        <input id="onb-f-pin1" type="password" inputmode="numeric" maxlength="4" autocomplete="new-password" data-pin="1"></div>
      <div class="onb-f__campo"><label for="onb-f-pin2">Repítelo</label>
        <input id="onb-f-pin2" type="password" inputmode="numeric" maxlength="4" autocomplete="new-password" data-pin="2"></div>`;
    $pie.innerHTML = '<button type="button" class="primario" data-accion="pin">Guardar mi PIN</button>';
  }

  function pintarFinal(texto) {
    pintarEncabezado('Listo', pasos.length);
    $cuerpo.innerHTML = `<div class="onb-f__final" data-onboard-final>${esc(texto)}</div><div data-cuenta-panel></div>`;
    $pie.innerHTML = '';
    ofrecerCuentaPanel($cuerpo.querySelector('[data-cuenta-panel]'));
  }

  // ONB-CUENTA-PANEL-T1 (contrato de A #33419): al cerrar, el dueno elige correo y clave para entrar al panel. Solo si
  // la ruta ya esta servida (sin servir, 404: no se muestra nada). La clave no entra a captured ni al borrador.
  async function ofrecerCuentaPanel($host) {
    if (!$host || !pinPublicadoSegun(await sondearCuentaPanel()) || !$host.isConnected) return;
    const pintar = (mensaje, esError) => {
      $host.innerHTML = `<div class="onb-f__cuenta" data-cuenta-form style="margin-top:18px">
        <div style="font-weight:600;margin-bottom:6px">Tu cuenta del panel</div>
        <div class="onb-f__regla" style="margin-bottom:10px">Con este correo y esta clave entras al panel de tu negocio. La clave necesita al menos ${CLAVE_MINIMA} caracteres.</div>
        ${mensaje ? `<div class="${esError ? 'onb-f__error' : 'onb-f__final'}" data-cuenta-mensaje style="margin-bottom:12px">${esc(mensaje)}</div>` : ''}
        <div class="onb-f__campo"><label for="onb-cuenta-correo">Correo</label>
          <input id="onb-cuenta-correo" type="email" autocomplete="email" data-cuenta="correo"></div>
        <div class="onb-f__campo"><label for="onb-cuenta-clave">Clave</label>
          <input id="onb-cuenta-clave" type="password" autocomplete="new-password" data-cuenta="clave"></div>
        <div class="onb-f__campo"><label for="onb-cuenta-repetida">Repite la clave</label>
          <input id="onb-cuenta-repetida" type="password" autocomplete="new-password" data-cuenta="repetida"></div>
        <button type="button" class="primario" data-cuenta-crear>Crear mi cuenta</button>
      </div>`;
      $host.querySelector('[data-cuenta-crear]').addEventListener('click', crear);
    };
    const crear = async (e) => {
      const valor = (k) => $host.querySelector(`[data-cuenta="${k}"]`)?.value || '';
      const correo = valor('correo').trim();
      const clave = valor('clave');
      const errores = erroresDeLaCuenta({ correo, clave, repetida: valor('repetida') });
      if (errores.length) { pintar(errores.join(' '), true); $host.querySelector('[data-cuenta="correo"]').value = correo; return; }
      e.target.disabled = true;
      const r = leerRespuestaDeCuenta(await enviarCuentaPanel({ token, correo, clave }));
      // 410 ENLACE_VENCIDO (nexo-api 723): otro intento daria lo mismo, asi que no se vuelve a ofrecer el formulario.
      if (r.ok || r.code === 'CUENTA_YA_EXISTE' || r.code === 'ENLACE_VENCIDO') {
        $host.innerHTML = `<div class="onb-f__final" data-cuenta-lista style="margin-top:18px">${esc(r.mensaje)}</div>`;
        return;
      }
      pintar(r.mensaje, true);
      $host.querySelector('[data-cuenta="correo"]').value = correo;
    };
    pintar('', false);
  }

  // ONB-LOGO-T1 (nexo-api 621): campo opcional Logo en el paso Tu negocio. Solo con la ruta servida (el sondeo se hace
  // una vez); sin ella no aparece nada. El archivo no entra a captured, al borrador ni a responses: solo viaja en el POST.
  let sondeoDelLogo = null;
  let logoUrl = null;
  let logoCerrado = false;   // LOGO_YA_EXISTE o ENLACE_CERRADO: otro intento daria lo mismo
  async function ofrecerLogo($host) {
    if (!$host) return;
    sondeoDelLogo = sondeoDelLogo || sondearLogo();
    if (!logoServidoSegun(await sondeoDelLogo) || !$host.isConnected) return;
    // El enlace pone el primer logo y nunca lo reemplaza (nexo-api 621 cf5d7f6): ya subido, no se ofrece otro archivo.
    const pintar = (mensaje, esError, previa) => {
      const src = previa || logoUrl;
      const cerrado = !!logoUrl || logoCerrado;
      $host.innerHTML = `<div class="onb-f__campo" data-logo-campo>
        <label${cerrado ? '' : ' for="onb-f-logo"'}>Logo de tu negocio <small>(opcional)</small></label>
        ${src ? `<img src="${esc(src)}" alt="Tu logo" data-logo-previa style="display:block;max-width:160px;max-height:96px;margin:4px 0 8px;border-radius:6px;background:var(--text-on-accent)">` : ''}
        ${cerrado ? '' : '<input id="onb-f-logo" type="file" accept="image/png,image/jpeg" data-logo-archivo>'}
        <div class="onb-f__ayuda">${cerrado ? esc(LOGO_QUEDA) : 'PNG o JPEG de hasta 5 MB. Si no lo tienes a la mano, puedes seguir sin él.'}</div>
        ${mensaje ? `<div class="${esError ? 'onb-f__error' : 'onb-f__previa'}" data-logo-mensaje>${esc(mensaje)}</div>` : ''}
      </div>`;
      $host.querySelector('[data-logo-archivo]')?.addEventListener('change', subir);
    };
    const subir = async (e) => {
      const archivo = e.target.files?.[0];
      if (!archivo) return;
      let primerosBytes = null;
      try { primerosBytes = new Uint8Array(await archivo.slice(0, 8).arrayBuffer()); } catch { /* que decida el API */ }
      const noSube = porQueNoSubeElLogo(archivo, primerosBytes);
      if (noSube) { pintar(noSube, true); return; }
      let previa = null;
      try { previa = URL.createObjectURL(archivo); } catch { /* sin vista previa */ }
      pintar('Subiendo tu logo…', false, previa);
      $host.querySelector('[data-logo-archivo]').disabled = true;
      const r = leerRespuestaDeLogo(await enviarLogo(token, archivo));
      if (previa) URL.revokeObjectURL(previa);
      if (r.ok) logoUrl = r.url;
      if (r.code === 'LOGO_YA_EXISTE' || r.code === 'ENLACE_CERRADO' || r.code === 'ENLACE_VENCIDO') logoCerrado = true;
      if ($host.isConnected) pintar(r.mensaje, !r.ok);
    };
    pintar('', false);
  }

  function leerCampo(el) {
    const clave = el.name;
    if (!clave || el.dataset.pin) return;
    if (el.type === 'checkbox') captured[clave] = el.checked;
    else if (el.type === 'radio') { if (el.checked) captured[clave] = el.value === 'si'; }
    else captured[clave] = el.value;
    if (clave === 'department') { captured.city = ''; }
    if (clave === 'cityOtra') captured.city = OTRA_CIUDAD;
    // Las horas arman el mismo texto de siempre ("Lun-Sáb 8am-8pm"); la previa lo muestra sin repintar.
    if (['horarioPlantilla', 'horarioAbre', 'horarioCierra'].includes(clave) && captured.horarioPlantilla && captured.horarioPlantilla !== 'personalizado') {
      captured.horario = textoDelHorario({ plantilla: captured.horarioPlantilla, abre: captured.horarioAbre, cierra: captured.horarioCierra }) || '';
      const $previa = $cuerpo.querySelector('[data-horario-previa]');
      if ($previa) $previa.textContent = captured.horario ? 'Quedará así: ' + captured.horario : '';
    }
    guardarBorrador();
    // Lo que cambia la forma del paso (departamento -> municipios, mismo celular -> telefono,
    // plantilla del horario -> horas o texto) se repinta; el resto no, para no perder el foco del teclado.
    if (['department', 'mismoCelular', 'horarioPlantilla'].includes(clave)) pintarPaso(actual);
  }

  // Al salir del municipio queda el de la lista ("medellin" -> "Medellín"); mientras escribe, no se toca.
  function canonizarMunicipio(el) {
    const canon = municipioCanon(captured.department, el.value);
    if (canon && canon !== el.value) { captured.city = canon; el.value = canon; guardarBorrador(); }
  }

  $cuerpo.addEventListener('change', (e) => {
    leerCampo(e.target);
    if (e.target.name === 'city') canonizarMunicipio(e.target);
  });
  $cuerpo.addEventListener('input', (e) => {
    if (e.target.matches('input[type=text], input[type=tel], input[type=email], input[type=time]')) leerCampo(e.target);
  });
  $cuerpo.addEventListener('click', (e) => {
    const editar = e.target.closest('[data-editar]');
    if (editar) { pintarPaso(Number(editar.dataset.editar)); return; }
    // CLI-ACT-T1: corregir abre el campo (un dato enmascarado arranca vacio: se escribe completo); dejar lo vuelve a la ficha.
    const $corregir = e.target.closest('[data-corregir]');
    if ($corregir) {
      const clave = $corregir.dataset.corregir;
      corregidos = corregir(corregidos, clave);
      if (ENMASCARADOS.includes(clave)) delete captured[clave];
      guardarCorregidos(); guardarBorrador();
      pintarPaso(actual);
      $cuerpo.querySelector(`[name="${clave}"]`)?.focus();
      return;
    }
    const $dejar = e.target.closest('[data-dejar]');
    if ($dejar) {
      const clave = $dejar.dataset.dejar;
      const antes = new Set(corregidos);
      corregidos = dejarComoEstaba(corregidos, clave);
      for (const k of antes) if (!corregidos.has(k)) { const v = valorAlDejar(ficha, k); if (v === undefined) delete captured[k]; else captured[k] = v; }
      guardarCorregidos(); guardarBorrador();
      pintarPaso(actual);
      return;
    }
    // El municipio que no esta en la lista se escribe aparte (y lo ya escrito se lleva).
    if (e.target.closest('[data-ciudad-otra]')) {
      const escrito = String(captured.city || '').trim();
      if (escrito && !municipioCanon(captured.department, escrito)) captured.cityOtra = escrito;
      captured.city = OTRA_CIUDAD;
      guardarBorrador();
      pintarPaso(actual);
      $cuerpo.querySelector('#onb-f-cityOtra')?.focus();
      return;
    }
    if (e.target.closest('[data-ciudad-lista]')) {
      captured.city = '';
      guardarBorrador();
      pintarPaso(actual);
      $cuerpo.querySelector('#onb-f-city')?.focus();
    }
  });

  $pie.addEventListener('click', async (e) => {
    const accion = e.target.closest('[data-accion]')?.dataset.accion;
    if (!accion) return;
    if (accion === 'atras') { pintarPaso(Math.max(0, actual - 1)); return; }
    if (accion === 'siguiente') {
      const errores = erroresDe(actual);
      if (Object.keys(errores).length) { pintarPaso(actual, errores); return; }
      // Retoma entre dispositivos: lo del paso viaja ya (fire-and-forget); el borrador es respaldo.
      const claves = pasos[actual].campos.map((c) => c.clave);
      submitResponses(token, cuerpo(claves));
      const siguiente = actual + 1;
      if (siguiente < pasos.length) pintarPaso(siguiente); else pintarResumen();
      return;
    }
    if (accion === 'confirmar') { await confirmar(e.target); return; }
    if (accion === 'pin') { await guardarPin(e.target); }
  });

  async function confirmar($btn) {
    const falta = pendiente();
    if (falta < pasos.length) { pintarPaso(falta, erroresDe(falta)); return; }
    $btn.disabled = true;
    const r = await submitResponses(token, cuerpo());
    // #15604: el API rechazo una respuesta (correo o telefono): se vuelve a ese paso y se explica.
    const rechazada = r && r.status === 400 ? claveDelRechazo(r.json) : null;
    if (rechazada) {
      for (const k of clavesParaRepreguntar(rechazada)) delete captured[k];
      // CLI-ACT-T1: en confirmacion el rechazo puede ser un dato VIEJO de la ficha: se abre para corregirlo.
      if (modo === 'confirmacion') { for (const k of clavesParaRepreguntar(rechazada)) if (ficha[k]) corregidos = corregir(corregidos, k); guardarCorregidos(); }
      guardarBorrador();
      const i = Math.max(0, pasoDeLaClave(pasos, rechazada));
      pintarPaso(i, { [rechazada]: MENSAJES_DE_CONTACTO[rechazada] || 'Revisa este dato.' });
      return;
    }
    if (!r) { $btn.disabled = false; pintarResumen('No pudimos enviar: parece que no hay conexión. Tus datos quedaron en este celular; intenta otra vez.'); return; }
    // Ares #38558: solo un 2xx sigue al cierre. Antes un 404/401/5xx caia en "Listo, ya tengo todo" sin haber guardado.
    const desenlace = desenlaceDelEnvio(r);
    if (desenlace === 'enlace_vencido') { pintarResumen(ENLACE_VENCIDO); return; }
    if (desenlace !== 'guardado') { $btn.disabled = false; pintarResumen(NO_SE_GUARDO); return; }
    // El PIN solo sobre una sesion completa y con el endpoint servido (400 con cuerpo vacio).
    if (PIN_ONBOARD === 'apagado' || !sesionCompleta(r.json)) { pintarFinal(CIERRE_SIN_PIN); return; }
    if (!pinPublicadoSegun(await sondearPin())) { pintarFinal(CIERRE_SIN_PIN); return; }
    conversacionPin = crearConversacionDelPin({ token, enviar: enviarPin });
    conversacionPin.inicio();
    pintarPin();
  }

  // El PIN: se lee de las cajas y se BORRAN antes de cualquier espera; no toca `captured`,
  // ni el borrador, ni responses. La conversacion del modulo decide que decir.
  async function guardarPin($btn) {
    const $p1 = $cuerpo.querySelector('[data-pin="1"]');
    const $p2 = $cuerpo.querySelector('[data-pin="2"]');
    const uno = String($p1?.value || '').trim();
    const dos = String($p2?.value || '').trim();
    if ($p1) $p1.value = '';
    if ($p2) $p2.value = '';
    if (!uno || !dos) { pintarPin('Escribe tu PIN en las dos cajas.'); return; }
    $btn.disabled = true;
    const primero = await conversacionPin.responder(uno);
    if (primero.fase !== 'repetir') { pintarPin(mensajeDelPin(primero)); return; }
    const segundo = await conversacionPin.responder(dos);
    if (segundo.caja === 'cerrada') { pintarFinal(mensajeDelPin(segundo)); return; }
    pintarPin(mensajeDelPin(segundo));
  }

  // Lo que dice la conversacion, sin el eco (****) ni la pregunta de siempre.
  const mensajeDelPin = (r) => (r.mensajes || [])
    .filter((m) => m.quien !== 'me' && m.texto !== PREGUNTA_PIN && m.texto !== ECO_PIN)
    .map((m) => m.texto).join(' ');

  if (actual < pasos.length) pintarPaso(actual); else pintarResumen();
}

// OB-T1: el borrador persiste en backend (merge por campo, idempotente al completar).
// Devuelve {status, json} para que el cierre sepa si la sesion quedo completa, o null sin red.
async function submitResponses(token, payload) {
  try {
    const res = await OnboardingPublicoApiClient.pedir(`/api/v1/onboarding/responses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ token, payload })
    });
    let json = null;
    try { json = await res.json(); } catch { /* sin cuerpo */ }
    return { status: res.status, json };
  } catch {
    return null;
  }
}

// ¿Ya existe POST /onboarding/pin? Con cuerpo vacio: servido contesta 400,
// sin servir 404. No lleva ningun dato del cliente.
async function sondearPin() {
  try {
    const res = await OnboardingPublicoApiClient.pedir(`/api/v1/onboarding/pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: '{}'
    });
    return res.status;
  } catch {
    return 0;
  }
}

// ONB-CUENTA-PANEL-T1: ¿ya existe POST /onboarding/cuenta-panel? Igual que el PIN: cuerpo vacio, 400 servido, 404 no.
async function sondearCuentaPanel() {
  try {
    const res = await OnboardingPublicoApiClient.pedir(`/api/v1/onboarding/cuenta-panel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: '{}'
    });
    return res.status;
  } catch {
    return 0;
  }
}

// ONB-LOGO-T1: ¿ya existe POST /onboarding/logo? Un multipart sin token ni archivo: servida contesta 400 TOKEN_REQUERIDO
// (A lee el formulario a mano, nunca 415), sin servir 404. No lleva ningun dato del dueno.
async function sondearLogo() {
  try {
    const res = await OnboardingPublicoApiClient.pedir(`/api/v1/onboarding/logo`, { method: 'POST', body: new FormData() });
    let json = null;
    try { json = await res.json(); } catch { /* sin cuerpo */ }
    return { status: res.status, cuerpo: json };
  } catch {
    return null;
  }
}

// El UNICO viaje del logo: multipart con token y archivo (sin Content-Type a mano: el navegador pone el boundary).
async function enviarLogo(token, archivo) {
  try {
    const form = new FormData();
    form.append('token', token);
    form.append('archivo', archivo);
    const res = await OnboardingPublicoApiClient.pedir(`/api/v1/onboarding/logo`, { method: 'POST', body: form });
    let json = null;
    try { json = await res.json(); } catch { /* sin cuerpo */ }
    return { status: res.status, cuerpo: json };
  } catch {
    return null;
  }
}

// El UNICO viaje de la clave del panel. Nunca lanza: sin red devuelve null.
async function enviarCuentaPanel(cuerpo) {
  try {
    const res = await OnboardingPublicoApiClient.pedir(`/api/v1/onboarding/cuenta-panel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(cuerpo)
    });
    let json = null;
    try { json = await res.json(); } catch { /* 204 no trae cuerpo */ }
    return { status: res.status, cuerpo: json };
  } catch {
    return null;
  }
}

// El UNICO viaje del PIN.
async function enviarPin(cuerpo) {
  const res = await OnboardingPublicoApiClient.pedir(`/api/v1/onboarding/pin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify(cuerpo)
  });
  let json = null;
  try { json = await res.json(); } catch { /* 204 no trae cuerpo */ }
  return { status: res.status, cuerpo: json, retryAfter: res.headers.get('Retry-After') };
}

function fieldLabel(key) {
  const m = {
    displayName: 'el nombre del negocio',
    legalName: 'la razón social',
    taxId: 'el NIT',
    businessType: 'el tipo de negocio',
    addressLine1: 'la dirección',
    department: 'el departamento',
    city: 'la ciudad',
    ownerName: 'tu nombre',
    ownerId: 'tu cédula',
    ownerEmail: 'tu correo',
    ownerPhone: 'tu celular',
    horario: 'el horario'
  };
  return m[key] || key;
}

function _showExitModal(token) {
  const url = `${window.location.origin}${window.location.pathname}#/onboard/${encodeURIComponent(token)}`;
  const $modal = document.createElement('div');
  $modal.className = 'modal-backdrop';
  $modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px';
  $modal.innerHTML = `
    <div style="background:var(--bg-secondary);border:1px solid var(--border-default);border-radius:10px;padding:20px;max-width:520px;width:100%;box-sizing:border-box">
      <h3 style="margin:0 0 10px;color:var(--text-heading)">Guarda tu enlace privado</h3>
      <p style="color:var(--text-heading);font-size:0.875rem;margin:0 0 10px">
        Puedes retomar el registro más tarde usando este enlace. Lo que ya llenaste queda guardado.
      </p>
      <input type="text" readonly value="${esc(url)}" class="form-input" id="onboard-url-input" style="font-size:16px;width:100%;box-sizing:border-box" />
      <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px">
        <button class="btn-ghost btn-sm" id="onboard-exit-close">Cerrar</button>
        <button class="btn-primary btn-sm" id="onboard-exit-copy">Copiar enlace</button>
      </div>
    </div>`;
  document.body.appendChild($modal);
  const close = () => { document.body.removeChild($modal); };
  $modal.addEventListener('click', (e) => { if (e.target === $modal) close(); });
  $modal.querySelector('#onboard-exit-close').addEventListener('click', close);
  $modal.querySelector('#onboard-exit-copy').addEventListener('click', async () => {
    const $btn = $modal.querySelector('#onboard-exit-copy');
    try {
      await navigator.clipboard.writeText(url);
      $btn.textContent = 'Copiado ✓';
      setTimeout(close, 900);
    } catch {
      const $input = $modal.querySelector('#onboard-url-input');
      $input.select();
      document.execCommand('copy');
      $btn.textContent = 'Copiado ✓';
      setTimeout(close, 900);
    }
  });
}
